import React, { memo, useMemo } from 'react';
import { Box, InputBase, makeStyles } from '@material-ui/core';
import _ from 'lodash';
import { TrolleyIcon } from '../../../../../components/Icon';
import { E_ADMISSION_GRID_SIMENSIONS } from '../../../../../comm/constant';
import PatientBed from './PatientBed';

const getPatientEaId = (patient) => patient?.eaId ?? null;

// 只比较当前床是否选中，避免 selectedBed 对象变化导致所有床重渲染。
const getBedSelected = ({ editMode, isQueuebar, selectedPatient, selectedBed, cubicleId, bedData }) => {
  if (editMode) return false;

  if (isQueuebar) {
    return Boolean(selectedPatient) && getPatientEaId(selectedPatient) === getPatientEaId(bedData);
  }

  return Boolean(selectedBed) && selectedBed?.cubicleId === cubicleId && selectedBed?.key === bedData?.key;
};

const useStyles = makeStyles(() => ({
  root: ({ isQueuebar }) => ({
    width: E_ADMISSION_GRID_SIMENSIONS?.width,
    // minWidth: E_ADMISSION_GRID_SIMENSIONS?.width,
    height: E_ADMISSION_GRID_SIMENSIONS?.height,
    // minHeight: E_ADMISSION_GRID_SIMENSIONS?.height,
    position: 'relative',
  }),
  button: {
    width: '100%',
    height: '100%',
    padding: 0,
    borderRadius: '8px',
    display: 'block',
    textAlign: 'inherit',
  },
  editCard: () => ({
    width: '100%',
    height: '100%',
    boxSizing: 'border-box',
    borderRadius: '8px',
    border: 'solid 1px #bad2ff',
    backgroundColor: '#ffffff',
    boxShadow: 'none',
    padding: '0.285rem',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  }),
  selectedCard: {
    // boxShadow: '0 0 0 2px #538fff',
    border: 'solid 2px #538fff',
  },
  bedNoInput: {
    width: '3.857rem',
    height: '2.5rem',
    boxSizing: 'border-box',
    border: 'solid 1px #bad2ff',
    borderRadius: '8px',
    backgroundColor: '#ffffff',
    fontFamily: 'NotoSans-Bold',
    fontSize: '1rem',
    lineHeight: 1.2,
    color: '#2e4469',
    '& input': {
      height: '100%',
      padding: '0 0.286rem',
      boxSizing: 'border-box',
      textAlign: 'center',
    },
  },
  bedNoInputWithPatient: {
    width: '3.857rem',
    height: '2.5rem',
    boxSizing: 'border-box',
    fontFamily: 'NotoSans-Bold',
    fontSize: '1.143rem',
    color: '#2e4469',
    '& input': {
      height: '100%',
      boxSizing: 'border-box',
      textAlign: 'center',
    },
  },
  bedNoPlate: ({ bedNoBorderColor }) => ({
    width: '3.4rem',
    height: '2.5rem',
    boxSizing: 'border-box',
    borderLeft: `solid 1px ${bedNoBorderColor}`,
    borderBottom: `solid 1px ${bedNoBorderColor}`,
    borderTop: 'solid 1px transparent',
    borderRight: 'solid 1px transparent',
    borderRadius: '1px 1px 1px 4px',
    backgroundColor: '#ffffff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  }),
  bedNoText: {
    fontFamily: 'NotoSans-Bold',
    fontSize: '1rem',
    lineHeight: 1.2,
    color: '#2e4469',
    // whiteSpace: 'nowrap',
  },
  editTrolleyIconBox: {
    width: '3.714rem',
    height: '2.104rem',
  },
  editRightSpacer: {
    width: '6.857rem',
    height: '2.5rem',
    flexShrink: 0,
  },
}));

const stopInputEvent = (event) => {
  event.stopPropagation();
};

function EADBed({
  bedData,
  handleUpdateBedNo,
  cubicleVersion = 0,
  editMode = false,
  isQueuebar = false,
  selectedBed = null,
  selectedPatient = null,
  cubicleId = null,
  handleSelectBed = null,
  handleSelectPatient = null,
  handleDeleteBedPatient = null,
  handleOpenPatientDetail = null,
  viewType = '',
  showDetails = true,
  patientInfoViewType = '',
}) {
  const hasPatient = useMemo(() => {
    if (editMode) {
      return bedData?.isOccupied ?? false;
    } else {
      return isQueuebar || Boolean(bedData?.patientInfo);
    }
  }, [bedData]);
  const classes = useStyles({
    bedNoBorderColor: !editMode && hasPatient ? '#538fff' : '#bad2ff',
    isQueuebar,
  });
  const isSelected = useMemo(() => {
    return getBedSelected({ editMode, isQueuebar, selectedPatient, selectedBed, cubicleId, bedData });
  }, [bedData, cubicleId, editMode, isQueuebar, selectedBed, selectedPatient]);
  const gridStyle = useMemo(() => {
    if (editMode || isQueuebar) {
      return undefined;
    }

    return {
      gridColumn: bedData?.column,
      gridRow: bedData?.row,
    };
  }, [bedData?.column, bedData?.row, editMode, isQueuebar]);

  const handleSelect = (type) => {
    // setting page没有选中事件
    if (editMode) {
      return;
    }
    // 在Queuebar点击的时候记录当前点击的病人eaId到selectedPatient用于绑定到病床
    if (isQueuebar && _.isFunction(handleSelectPatient)) {
      handleSelectPatient(bedData);
      return;
    }

    // 在cubicle的时候点击病床记录当前点击的床位信息用于绑定病人到该病床
    handleSelectBed({ cubicleId, bedData: { ...bedData, cubicleVersion }, hasPatient });
  };

  const handleDeletePatient = () => {
    if (!_.isFunction(handleDeleteBedPatient)) {
      return;
    }

    handleDeleteBedPatient({ cubicleId, bedData, cubicleVersion });
  };

  const handleOpenDetail = (patientInfo) => {
    if (!_.isFunction(handleOpenPatientDetail)) {
      return;
    }

    handleOpenPatientDetail({ cubicleId, bedData, cubicleVersion, patientInfo });
  };

  const renderEmptyBed = () => (
    <Box className={classes.editCard}>
      <InputBase
        className={classes.bedNoInput}
        value={bedData?.bedName ?? ''}
        onChange={(event) => handleUpdateBedNo(event.target.value, bedData)}
        onClick={stopInputEvent}
        onMouseDown={stopInputEvent}
        inputProps={{ maxLength: 10, 'aria-label': 'Bed No.' }}
      />
    </Box>
  );

  const renderAvailableBed = () => (
    <Box
      className={`${classes.editCard}`}
      onClick={handleSelect}
      onMouseDown={stopInputEvent}
      style={{
        border: isSelected ? 'solid 2px #538fff' : '',
      }}
    >
      <Box className={classes.bedNoPlate}>
        {editMode ? (
          <InputBase
            className={classes.bedNoInputWithPatient}
            value={bedData?.bedName ?? ''}
            onChange={(event) => handleUpdateBedNo(event.target.value, bedData)}
            onClick={stopInputEvent}
            onMouseDown={stopInputEvent}
            inputProps={{ maxLength: 10, 'aria-label': 'Bed No.' }}
          />
        ) : (
          <span className={classes.bedNoText}>{bedData?.bedName}</span>
        )}
      </Box>
      <TrolleyIcon className={classes.editTrolleyIconBox} />
    </Box>
  );

  const renderPatientBed = () => (
    <PatientBed
      patientInfo={isQueuebar ? bedData : bedData?.patientInfo}
      editMode={editMode}
      hasPatient={hasPatient}
      isQueuebar={isQueuebar}
      isSelected={isSelected}
      handleSelect={handleSelect}
      handleDeletePatient={handleDeletePatient}
      handleOpenPatientDetail={handleOpenDetail}
      bedName={bedData?.bedName}
      viewType={viewType}
      showDetails={showDetails}
      patientInfoViewType={patientInfoViewType}
    />
  );

  const editModeBed = hasPatient ? renderAvailableBed() : renderEmptyBed();
  const viewModeBed = hasPatient ? renderPatientBed() : renderAvailableBed();

  return (
    <Box className={classes.root} style={gridStyle}>
      {editMode ? editModeBed : viewModeBed}
    </Box>
  );
}

// 选中状态没变的床不重渲染，只更新当前和上一个选中的床。
const areEqual = (prevProps, nextProps) => {
  return (
    prevProps.bedData === nextProps.bedData &&
    prevProps.cubicleVersion === nextProps.cubicleVersion &&
    prevProps.editMode === nextProps.editMode &&
    prevProps.isQueuebar === nextProps.isQueuebar &&
    prevProps.cubicleId === nextProps.cubicleId &&
    prevProps.handleUpdateBedNo === nextProps.handleUpdateBedNo &&
    prevProps.handleSelectBed === nextProps.handleSelectBed &&
    prevProps.handleSelectPatient === nextProps.handleSelectPatient &&
    prevProps.handleDeleteBedPatient === nextProps.handleDeleteBedPatient &&
    prevProps.handleOpenPatientDetail === nextProps.handleOpenPatientDetail &&
    prevProps.viewType === nextProps.viewType &&
    prevProps.showDetails === nextProps.showDetails &&
    prevProps.patientInfoViewType === nextProps.patientInfoViewType &&
    getBedSelected(prevProps) === getBedSelected(nextProps)
  );
};

export default memo(EADBed, areEqual);
